import type { GetUndefinedSymbols, UndefinedSymbols } from '../../getUndefinedSymbols'
import type { ContextStack } from '../../evaluator/ContextStack'
import type { Context } from '../../evaluator/interface'
import type { Any } from '../../interface'
import type { AstNode, BindingTarget } from '../../parser/types'
import type { NodeTypes } from '../../constants/constants'
import type { Builtin, BuiltinSpecialExpression, CustomDocs } from '../interface'
import { getAllBindingTargetNames } from '../bindingNode'
import { toFixedArity } from '../../utils/arity'

export type LoopBindingNode = [[BindingTarget, AstNode], [BindingTarget, AstNode][], AstNode?, AstNode?] // Binding, Let-Bindings, When, While

export type ForNode = [typeof NodeTypes.For, [LoopBindingNode[], AstNode], number] // LoopBindings, body

function analyze(
  loopNode: ForNode,
  contextStack: ContextStack,
  getUndefinedSymbols: GetUndefinedSymbols,
  builtin: Builtin,
): UndefinedSymbols {
  const result = new Set<string>()
  const newContext: Context = {}
  const [loopBindings, body] = loopNode[1]
  loopBindings.forEach(loopBindingNode => {
    const [binding, letBindings, whenNode, whileNode] = loopBindingNode
    const [target, value] = binding
    getUndefinedSymbols([value], contextStack.create(newContext), builtin).forEach(symbol =>
      result.add(symbol),
    )
    Object.assign(newContext, getAllBindingTargetNames(target))
    // Defensive: letBindings is always present when parsed from loop syntax
    /* v8 ignore next */
    if (letBindings) {
      letBindings.forEach(letBinding => {
        const [letTarget, letValue] = letBinding

        getUndefinedSymbols([letValue], contextStack.create(newContext), builtin).forEach(symbol =>
          result.add(symbol),
        )
        Object.assign(newContext, getAllBindingTargetNames(letTarget))
      })
    }
    if (whenNode) {
      getUndefinedSymbols([whenNode], contextStack.create(newContext), builtin).forEach(symbol =>
        result.add(symbol),
      )
    }
    if (whileNode) {
      getUndefinedSymbols([whileNode], contextStack.create(newContext), builtin).forEach(symbol =>
        result.add(symbol),
      )
    }
  })
  getUndefinedSymbols([body], contextStack.create(newContext), builtin).forEach(symbol =>
    result.add(symbol),
  )
  return result
}

const forDocs: CustomDocs = {
  category: 'special-expression',
  customVariants: ['for (...binding) -> body'],
  details: [
    ['binding', 'loop-var in collection [...let-binding] [where whereExpr] [while whileExp]', 'A for loop binding'],
    ['loop-var', 'symbol', 'The name of the loop variable.'],
    ['collection', 'any', 'The collection to iterate over.'],
    ['let-binding', 'let binding', 'A let binding to create a local variable.'],
    ['whereExpr', 'expression', 'An expression that must evaluate to truthy for the loop body to be executed.'],
    ['whileExp', 'expression', 'An expression that must evaluate to truthy for the loop to continue.'],
    ['body', 'expressions', 'The expressions to evaluate for each iteration of the loop.'],
  ],
  returns: {
    type: 'any',
    array: true,
  },
  description: 'Iterates over `bindings`, evaluates `body` for each `binding` and returns an `array` of results.',
  examples: [
    `
for (i in [1, 2, 3]) -> i * 2
      `,
    `
for (
  i in range(10) let ii = i ^ 2 while ii < 40 when ii % 3 == 0,
  j in range(10) when j % 2 == 1
) -> ii + j
      `,
  ],
}

export const forSpecialExpression: BuiltinSpecialExpression<Any, ForNode> = {
  arity: toFixedArity(1),
  docs: forDocs,
  getUndefinedSymbols: (node, contextStack, { getUndefinedSymbols, builtin }) => analyze(node, contextStack, getUndefinedSymbols, builtin),
}
